// Performance detection and management utility
const PerformanceManager = {
  // Performance threshold in FPS
  LOW_PERFORMANCE_THRESHOLD: 45,
  
  // Performance features that can be disabled
  features: {
    shaders: true,
    particleEffects: true,
    animationQuality: 'high' // high, medium, low
  },
  
  // Stores if we're on a low performance device
  isLowPerformanceDevice: false,
  
  // Tracks frame rate
  frameRateHistory: [],
  lastFrameTime: 0,
  frameRateCheckRunning: false,
  
  // Detect performance on initialization
  init() {
    // Initial device check based on platform and hardware
    this.performInitialDeviceCheck();
    
    // Start monitoring actual performance
    this.startPerformanceMonitoring();
    
    return this.getFeatureSettings();
  },
  
  // Helper to detect Android version
  getAndroidVersion() {
    const userAgent = navigator.userAgent;
    const match = userAgent.match(/Android\s([0-9\.]+)/);
    return match ? parseFloat(match[1]) : null;
  },
  
  // Do initial quick checks for known low-end device factors
  performInitialDeviceCheck() {
    // Check CPU cores
    const cpuCores = navigator.hardwareConcurrency || 4;
    
    // Check device memory (only works in Chrome)
    const deviceMemory = navigator.deviceMemory || 4;
    
    // Check for mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // NEW: Check for Android version below 14
    const androidVersion = this.getAndroidVersion();
    const isOldAndroid = androidVersion !== null && androidVersion < 14;
    
    // Enable low performance mode immediately for older Android devices
    if (isOldAndroid) {
      console.log(`Android ${androidVersion} detected - activating low performance mode`);
      this.setLowPerformanceMode(true);
      return; // Exit early as we've already made the decision
    }
    
    // Create a score based on these factors
    let performanceScore = 0;
    if (cpuCores <= 2) performanceScore += 2;
    if (deviceMemory <= 2) performanceScore += 2;
    if (isMobile) performanceScore += 1;
    
    // Set device as low performance if score is high
    if (performanceScore >= 3) {
      this.setLowPerformanceMode(true);
    }
  },
  
  // Start monitoring actual FPS
  startPerformanceMonitoring() {
    if (this.frameRateCheckRunning) return;
    
    this.frameRateCheckRunning = true;
    this.lastFrameTime = performance.now();
    
    const checkFrame = () => {
      const now = performance.now();
      const deltaTime = now - this.lastFrameTime;
      const currentFPS = 1000 / deltaTime;
      
      this.frameRateHistory.push(currentFPS);
      if (this.frameRateHistory.length > 60) {
        this.frameRateHistory.shift();
        
        // Calculate average FPS
        const avgFPS = this.frameRateHistory.reduce((sum, fps) => sum + fps, 0) / this.frameRateHistory.length;
        
        // Check if performance is low
        if (avgFPS < this.LOW_PERFORMANCE_THRESHOLD && !this.isLowPerformanceDevice) {
          this.setLowPerformanceMode(true);
        }
      }
      
      this.lastFrameTime = now;
      
      // Continue monitoring if not suspended
      if (this.frameRateCheckRunning) {
        requestAnimationFrame(checkFrame);
      }
    };
    
    requestAnimationFrame(checkFrame);
  },
  
  stopPerformanceMonitoring() {
    this.frameRateCheckRunning = false;
  },
  
  // Switch to low performance mode
  setLowPerformanceMode(isLow) {
    if (this.isLowPerformanceDevice === isLow) return;
    
    this.isLowPerformanceDevice = isLow;
    
    if (isLow) {
      // Disable or reduce expensive features
      this.features.shaders = false;
      this.features.particleEffects = false;
      this.features.animationQuality = 'low';
      
      // Throttle game speed slightly to improve performance
      // You might need to adjust the scrollSpeed in Beats.js
      console.log('Low performance mode activated');
    } else {
      // Re-enable features for high-end devices
      this.features.shaders = true;
      this.features.particleEffects = true;
      this.features.animationQuality = 'high';
      console.log('Normal performance mode activated');
    }
    
    // Publish performance change event for components to listen to
    window.dispatchEvent(new CustomEvent('performanceChange', {
      detail: { isLowPerformance: isLow, features: this.features }
    }));
  },
  
  // Get current feature settings
  getFeatureSettings() {
    return { ...this.features, isLowPerformanceDevice: this.isLowPerformanceDevice };
  }
};

export default PerformanceManager; 